import { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { toast } from "sonner";
import { IncidentReminderSettings } from "./incidents/IncidentReminderSettings";

export default function SlaSettingsDialog({ open, onClose }) {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) api.get("/sla-settings").then(({ data }) => setS(data)).catch(() => setS(null));
  }, [open]);

  const setDet = (k, v) => setS((p) => ({ ...p, determination_days: { ...p.determination_days, [k]: v } }));

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/sla-settings", {
        triage_working_days: Number(s.triage_working_days) || 10,
        determination_days: {
          high: Number(s.determination_days.high) || 60,
          medium: Number(s.determination_days.medium) || 60,
          low: Number(s.determination_days.low) || 60,
        },
      });
      setS(data); toast.success("SLA settings saved."); onClose();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Save failed."); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-none max-w-md" data-testid="sla-settings-dialog">
        <DialogHeader><DialogTitle>SLA & Aging Settings</DialogTitle></DialogHeader>
        {!s ? <p className="text-sm text-muted-foreground py-4">Loading…</p> : (
          <div className="space-y-4">
            <div>
              <Label className="text-xs">Incident triage deadline (working days from discovery)</Label>
              <Input type="number" min={1} value={s.triage_working_days}
                onChange={(e) => setS((p) => ({ ...p, triage_working_days: e.target.value }))}
                className="rounded-none mt-1 w-32" data-testid="sla-triage-days" />
              <p className="text-[11px] text-muted-foreground mt-1">Every discovered/reported issue should be triaged within this window.</p>
            </div>
            <div>
              <Label className="text-xs">PNC determination deadline (calendar days) — by VRF/VSL priority</Label>
              <div className="grid grid-cols-3 gap-2 mt-1">
                <div><Label className="text-[11px] text-red-600">High</Label><Input type="number" min={1} value={s.determination_days.high} onChange={(e) => setDet("high", e.target.value)} className="rounded-none" data-testid="sla-det-high" /></div>
                <div><Label className="text-[11px] text-amber-700">Medium</Label><Input type="number" min={1} value={s.determination_days.medium} onChange={(e) => setDet("medium", e.target.value)} className="rounded-none" data-testid="sla-det-medium" /></div>
                <div><Label className="text-[11px] text-slate-600">Low</Label><Input type="number" min={1} value={s.determination_days.low} onChange={(e) => setDet("low", e.target.value)} className="rounded-none" data-testid="sla-det-low" /></div>
              </div>
              <p className="text-[11px] text-muted-foreground mt-1">Priority is scored from the VRF × VSL combination (falls back to the risk assessment). Default is 60 days for all.</p>
            </div>
            <IncidentReminderSettings />
            <div className="flex justify-end gap-2">
              <Button variant="outline" className="rounded-none" onClick={onClose}>Cancel</Button>
              <Button className="rounded-none" onClick={save} disabled={busy} data-testid="sla-settings-save">{busy ? "Saving…" : "Save SLA deadlines"}</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
